import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { store } from '../../redux/oldStore';
import { RegistrationProgressResponse } from '../../utils/CommonTypes/UserTypes';
import { ServerResponse } from '../../utils/Server';
import Icon, { IconType } from '../Icon/Icon';

export type RegisterStepKey = 'profileInfo' | 'project' | 'requirements' | 'medical' | 'store' | 'done';

interface RegistrationStepperProps {
    personId: string;
    currentStep: RegisterStepKey;
    currentSubTabIndex?: number;
    currentSubTotalTabs?: number;
    onSubTabClick?: (tabIndex: number) => void;
}

const STEP_ORDER_TERMS_FIRST: RegisterStepKey[] = ['profileInfo', 'project', 'requirements', 'medical', 'store', 'done'];
const STEP_ORDER_MEDICAL_FIRST: RegisterStepKey[] = ['profileInfo', 'project', 'medical', 'requirements', 'store', 'done'];
const requirementsConfirmedKey = (personId: string) => `fms-requirements-confirmed-${personId}`;

const stepRoute = (step: RegisterStepKey, personId: string): string => {
    switch (step) {
        case 'profileInfo': return `/app/user/profile/${personId}`;
        case 'project': return `/app/project/${personId}`;
        case 'requirements': return `/app/user/requirements/${personId}`;
        case 'medical': return `/app/user/medical/${personId}`;
        case 'store': return `/app/store/${personId}`;
        case 'done': return `/app/user/done/${personId}`;
    }
};

const RegistrationStepper = (props: RegistrationStepperProps) => {
    const { personId, currentStep, currentSubTabIndex, currentSubTotalTabs, onSubTabClick } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const [progress, setProgress] = useState<RegistrationProgressResponse>();

    useEffect(() => {
        if (!personId) return;
        store.server.postApi<ServerResponse<RegistrationProgressResponse>>('../UserProfile/GetRegistrationProgress', { userId: personId }).then((x) => {
            if (x.Success) {
                setProgress(x.Value);
            }
        });
    }, [personId]);

    useEffect(() => {
        if (!personId || !progress) return;
        const key = requirementsConfirmedKey(personId);
        const gated: RegisterStepKey[] = progress.MedicalBeforeTerms ? ['store'] : ['medical', 'store'];
        if (currentStep === 'requirements') {
            sessionStorage.setItem(key, '1');
        } else if (gated.includes(currentStep) && sessionStorage.getItem(key) !== '1') {
            navigate(`${stepRoute('requirements', personId)}${location.search}`, { replace: true });
        }
    }, [personId, currentStep, progress]);

    if (!progress) return null;

    const stepOrder = progress.MedicalBeforeTerms ? STEP_ORDER_MEDICAL_FIRST : STEP_ORDER_TERMS_FIRST;
    const steps = stepOrder.filter((s) => {
        if (s === 'project') return progress.ProjectStepApplicable;
        if (s === 'medical') return progress.MedicalStepApplicable;
        return true;
    });

    const furthestIndex = steps.indexOf(progress.FurthestCompletedStep as RegisterStepKey);
    const currentIndex = steps.indexOf(currentStep);
    const requirementsIndex = steps.indexOf('requirements');

    const capActive = requirementsIndex !== -1 && currentIndex < requirementsIndex && furthestIndex > requirementsIndex;
    const effectiveFurthestIndex = capActive ? requirementsIndex : furthestIndex;

    const goToStep = (step: RegisterStepKey) => {
        navigate(`${stepRoute(step, personId)}${location.search}`);
    };

    const renderDot = (key: string, sectionClass: string, active: boolean, clickable: boolean, small: boolean, onClick: () => void, title: string) => (
        <div
            key={key}
            title={title}
            className={`stepper-dot ${sectionClass} ${active ? 'active' : ''} ${clickable ? 'clickable' : ''} ${small ? 'stepper-dot-sm' : ''}`}
            onClick={clickable ? onClick : undefined}
        />
    );

    const renderProfileInfoSection = () => {
        const i = steps.indexOf('profileInfo');
        const isCurrent = i === currentIndex;
        const isComplete = i <= effectiveFurthestIndex && !isCurrent;

        const totalTabs = currentSubTotalTabs ?? 1;
        const tabIndex = currentSubTabIndex ?? 0;
        const profileTabCount = totalTabs - 1;

        const accountVisited = isComplete || isCurrent;
        const accountClickable = isComplete || (isCurrent && tabIndex > 0 && !!onSubTabClick);
        const accountOnClick = isCurrent ? () => onSubTabClick?.(0) : () => goToStep('profileInfo');
        const accountDot = renderDot('profileInfo-account', 'stepper-section-account', accountVisited, accountClickable, false, accountOnClick, 'Account Creation');

        if (isCurrent && profileTabCount > 1 && tabIndex >= 1) {
            const profileDots = Array.from({ length: profileTabCount }).map((_, idx) => {
                const tabIdx = idx + 1;
                const active = tabIdx <= tabIndex;
                const clickable = tabIdx <= tabIndex && !!onSubTabClick;
                return renderDot(`profileInfo-profile-${idx}`, 'stepper-section-profile', active, clickable, true, () => onSubTabClick?.(tabIdx), `Profile ${idx + 1}`);
            });
            return { accountDot, profileDots };
        }

        const active = isComplete || isCurrent;
        return {
            accountDot,
            profileDots: [renderDot('profileInfo-profile', 'stepper-section-profile', active, isComplete, false, () => goToStep('profileInfo'), 'Profile')],
        };
    };

    const sectionMeta: Record<Exclude<RegisterStepKey, 'profileInfo'>, { cssClass: string; label: string }> = {
        project: { cssClass: 'stepper-section-entry', label: 'Entry' },
        requirements: { cssClass: 'stepper-section-requirements', label: 'Requirements' },
        medical: { cssClass: 'stepper-section-medical', label: 'Medical' },
        store: { cssClass: 'stepper-section-checkout', label: 'Checkout' },
        done: { cssClass: 'stepper-section-done', label: 'Done' },
    };

    const renderExpandableSection = (step: Exclude<RegisterStepKey, 'profileInfo'>, i: number) => {
        const meta = sectionMeta[step];
        const isCurrent = i === currentIndex;

        if (isCurrent && (currentSubTotalTabs ?? 1) > 1) {
            const totalTabs = currentSubTotalTabs;
            const tabIndex = currentSubTabIndex ?? 0;
            return Array.from({ length: totalTabs }).map((_, tabIdx) => {
                const active = tabIdx <= tabIndex;
                const clickable = tabIdx <= tabIndex && !!onSubTabClick;
                return renderDot(`${step}-${tabIdx}`, meta.cssClass, active, clickable, true, () => onSubTabClick?.(tabIdx), `${meta.label} ${tabIdx + 1}`);
            });
        }

        const active = i <= furthestIndex || isCurrent;
        const clickable = i <= effectiveFurthestIndex && !isCurrent;
        return [renderDot(step, meta.cssClass, active, clickable, false, () => goToStep(step), meta.label)];
    };

    const resumeIndex = capActive
        ? requirementsIndex
        : (furthestIndex >= 0 && furthestIndex + 1 < steps.length ? furthestIndex + 1 : -1);
    const canResume = resumeIndex !== -1 && resumeIndex > currentIndex;

    return (
        <>
            <div className="justify-content-center">
                <div className="stepper-container">
                    {steps.map((step, i) => {
                        if (step === 'profileInfo') {
                            const { accountDot, profileDots } = renderProfileInfoSection();
                            return (
                                <React.Fragment key={step}>
                                    <div className="stepper-section">{accountDot}</div>
                                    <div className="stepper-section">{profileDots}</div>
                                </React.Fragment>
                            );
                        }
                        return (
                            <div className="stepper-section" key={step}>
                                {renderExpandableSection(step, i)}
                            </div>
                        );
                    })}
                </div>
            </div>
            {canResume && (
                <div className="stepper-resume">
                    <button type="button" className="btn btn-link" onClick={() => goToStep(steps[resumeIndex])}>
                        Go to where you left off <Icon type={IconType.next} />
                    </button>
                </div>
            )}
        </>
    );
};

export default RegistrationStepper;
